// components/CreditBalanceCard.js
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import { AccountBalance, Add } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const CreditBalanceCard = ({ balance, loading }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  // Set threshold for low balance warning (example: 50)
  const lowBalanceThreshold = 50;
  const isLowBalance = balance < lowBalanceThreshold;

  const formatBalance = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(amount);

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "100%",
        border: `1px solid ${theme.palette.divider}`,
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[8],
        },
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          right: -100,
          top: -50,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: `linear-gradient(45deg, ${alpha(
            theme.palette.primary.main,
            0.15
          )}, ${alpha(theme.palette.primary.light, 0.05)})`,
          zIndex: 0,
        }}
      />

      <CardContent
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <Box
            sx={{
              bgcolor: theme.palette.primary.main,
              p: 1.5,
              // components/CreditBalanceCard.js (continued)
              borderRadius: 2,
              mr: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
            }}
          >
            <AccountBalance sx={{ color: "white" }} />
          </Box>
          <Typography variant="h6" fontWeight="bold">
            {t("credits.balance")}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            my: 2,
          }}
        >
          {loading ? (
            <CircularProgress size={50} />
          ) : (
            <>
              <Typography
                variant="h3"
                fontWeight="bold"
                color={isLowBalance ? "error.main" : "primary.main"}
              >
                {formatBalance(balance)}
              </Typography>

              {isLowBalance && (
                <Chip
                  label={t("credits.lowBalance")}
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<Add />}
          onClick={() => navigate("/topup")}
          sx={{
            mt: "auto",
            borderRadius: 2,
            py: 1.5,
            boxShadow: theme.shadows[4],
            "&:hover": {
              boxShadow: theme.shadows[8],
            },
          }}
        >
          {t("credits.topUp")}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CreditBalanceCard;
